import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { Dispatch, SetStateAction, memo, useState } from "react";
import Flex from "../../../../../components/Flex";
import GlobalFilter from "../../../../../components/GlobalFilter";
import LabeledSwitch from "../../../../../components/LabeledSwitch";
import Input from "../../../../../components/Input";
import Text from "../../../../../components/Text";
import CustomTabs from "../../../../../components/app/CustomTabs";
import StyledButtonV2 from "../../../../../components/app/StyledButtonV2";

import useDisclosure from "../../../../../hooks/use-disclosure";
import { useReduceDisclosure } from "../../../../../hooks/use-reduce-disclosure";
import { useProvidersList } from "../../../../../queries/provider/setting/use-providers-list";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getXtenantId,
  replaceUnderScoreAddSpace,
} from "../../../../../utils/utils";
import {
  DEFAULT_FILTERS,
  FILTER_KEYS,
  INITIAL_EXPANDED,
  TABS,
  TASK_ACTIONS,
  TaskFilters,
} from "../collaboration-task-constant";
import BatchReassignTaskDrawer from "./BatchReassignTaskDrawer";
import BatchResolveDrawer from "./BatchResolveDrawer";
import UpsertTaskDrawer from "./UpsertTaskDrawer";

import CustomDatePicker from "../../../../../components/CustomDatePicker";
import SearchInput from "../../../../../components/SearchInput";
import CustomSelect from "../../../../../components/CustomSelect";
import CustomSelectScroll from "../../../../../components/CustomSelectScroll";

import { useActiveComponentCountController } from "../../../../../components/filter/use-ActiveComponentCountController";
import { useDebounce } from "../../../../../hooks/use-debounce";
import ToggleTaskCalenderViewButton from "./task-calendar/ToggleTaskCalenderViewbutton";
import { ActiveTab } from "./Tasks";
interface TaskTopBarProps {
  patientData: any;
  activeTab: ActiveTab;
  setActiveTab: (v: ActiveTab) => void;
  setSearchParams: (v: { activeTask: ActiveTab }) => void;
  setTodoTaskFilter: Dispatch<
    SetStateAction<{ myTasks: boolean; toDoTask: boolean }>
  >;
  setExpanded: Dispatch<SetStateAction<Record<string, boolean>>>;
  setFilters: Dispatch<SetStateAction<TaskFilters>>;
  filters: TaskFilters;
  expanded: Record<string, boolean>;
  todoTaskFilter: { myTasks: boolean; toDoTask: boolean };
  selectedTaskModel: GridRowSelectionModel;
  setSelectedTaskModel: Dispatch<SetStateAction<any>>;
  setSearchStringKey?: (v: string) => void;
  searchStringKey?: string;
  setTaskType: Dispatch<SetStateAction<string>>;
  taskType: string;
  viewType?: "list" | "calender";
  setViewType?: Dispatch<SetStateAction<"list" | "calender">>;
}

const TaskTopBar = ({
  patientData,
  activeTab,
  setActiveTab,
  setSearchParams,
  setTodoTaskFilter,
  setExpanded,
  setFilters,
  filters,
  expanded,
  todoTaskFilter,
  selectedTaskModel,
  setSelectedTaskModel,
  setSearchStringKey,
  searchStringKey,
  setTaskType,
  taskType,
  viewType,
  setViewType,
}: TaskTopBarProps) => {
  return (
    <Flex
      justifyContent={"space-between"}
      alignItems={"center"}
      minHeight={"2.5rem"}
      pl={patientData ? 1 : 0}
      mr={patientData ? "12px" : 0}
      sx={{
        backgroundColor: patientData ? "#EEF7FF" : "transparent",
        flexWrap: "wrap",
      }}
    >
      {!patientData && (
        <CustomTabs
          tabsData={TABS}
          value={activeTab}
          onChange={(v) => {
            setActiveTab(v as ActiveTab);
            setSearchParams({
              activeTask: v as
                "patient-centric-tasks" | "provider-centric-tasks",
            });
          }}
        />
      )}

      <EndActions
        patientData={patientData}
        selectedTaskModel={selectedTaskModel}
        setTodoTaskFilter={setTodoTaskFilter}
        setExpanded={setExpanded}
        setFilters={setFilters}
        filters={filters}
        expanded={expanded}
        todoTaskFilter={todoTaskFilter}
        setSelectedTaskModel={setSelectedTaskModel}
        activeTab={activeTab}
        setSearchStringKey={setSearchStringKey}
        searchStringKey={searchStringKey}
        setTaskType={setTaskType}
        taskType={taskType}
        viewType={viewType}
        setViewType={setViewType}
      />
    </Flex>
  );
};

export default TaskTopBar;

interface EndActionsProps {
  setTodoTaskFilter: Dispatch<
    SetStateAction<{ myTasks: boolean; toDoTask: boolean }>
  >;
  patientData: any;
  setExpanded: Dispatch<SetStateAction<Record<string, boolean>>>;
  setFilters: Dispatch<SetStateAction<TaskFilters>>;
  filters: TaskFilters;
  expanded: Record<string, boolean>;
  todoTaskFilter: { myTasks: boolean; toDoTask: boolean };
  selectedTaskModel: GridRowSelectionModel;
  setSelectedTaskModel: Dispatch<SetStateAction<any>>;
  activeTab?: ActiveTab;
  setSearchStringKey?: (v: string) => void;
  searchStringKey?: string;
  setTaskType: Dispatch<SetStateAction<string>>;
  taskType: string;
  viewType?: "list" | "calender";
  setViewType?: Dispatch<SetStateAction<"list" | "calender">>;
}

const EndActions = memo(
  ({
    setTodoTaskFilter,
    patientData,
    setExpanded,
    setFilters,
    filters,
    expanded,
    todoTaskFilter,
    selectedTaskModel,
    setSelectedTaskModel,
    activeTab,
    setSearchStringKey,
    searchStringKey,
    setTaskType: _,
    viewType,
    setViewType,
    taskType,
  }: EndActionsProps) => {
    const taskDisclosureHost = useDisclosure();
    const [localFilters, setLocalFilters] = useState<TaskFilters>(filters);

    const activeFilterCount = useActiveComponentCountController(filters);

    const handleFilterChange =
      (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setTodoTaskFilter((prev) => ({
          ...prev,
          [key]: e.target.checked,
        }));
      };
    const [searchProviderKey, setSearchProviderKey] = useDebounce<string>("");
    const [providerPagination, setProviderPagination] =
      useState<DGT_Pagination>(DEF_PAGINATION);

    const { data: providersList, isFetching: providerFetching } =
      useProvidersList({
        pagination: providerPagination,
        searchKey: searchProviderKey,
        xTENANTID: getXtenantId(),
        enabled: expanded["assign_to"] || expanded["assign_by"],
      });

    const handleProviderSearch = (val: string) => {
      setSearchProviderKey(val);
      setProviderPagination((p) => ({ ...p, page: 0 }));
    };
    const handleApply = () => {
      setFilters(localFilters);
      setAnchorEl(null);
    };

    const handleCancel = () => {
      // setLocalFilters(DEFAULT_FILTERS);
      // setFilters(DEFAULT_FILTERS);
      setAnchorEl(null);
    };

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const handleExpandClick = (key: string, newExpanded: boolean) => {
      setExpanded({ ...INITIAL_EXPANDED, [key]: newExpanded });
    };

    const [batchResolveDisclosureHost, batchReassignDisclosureHost] =
      useReduceDisclosure(2);
    const isVisitNotePage = window.location.pathname.includes("/visit-note");

    return (
      <Flex
        gap={0.5}
        flex={1}
        alignItems={"center"}
        justifyContent={!patientData ? "end" : "space-between"}
        sx={{ flexWrap: "wrap" }}
      >
        <Flex
          marginLeft={"12px"}
          gap={1.5}
          alignItems={"center"}
          flexShrink={0}
        >
          <LabeledSwitch
            label="To-Do Tasks"
            checked={todoTaskFilter.toDoTask}
            onChange={handleFilterChange("toDoTask")}
            sx={{ mr: "10px" }}
          />
          <LabeledSwitch
            label="My Tasks"
            checked={todoTaskFilter.myTasks}
            onChange={handleFilterChange("myTasks")}
          />

          {taskDisclosureHost.opened && (
            <UpsertTaskDrawer
              isProviderCentric={activeTab === "provider-centric-tasks"}
              disclosureHost={taskDisclosureHost}
              taskType={taskType}
              patientData={patientData}
            />
          )}

          <BatchResolveDrawer
            disclosureHost={batchResolveDisclosureHost}
            setSelectedTaskModel={setSelectedTaskModel}
          />
          {batchReassignDisclosureHost.opened && (
            <BatchReassignTaskDrawer
              setSelectedTaskModel={setSelectedTaskModel}
              disclosureHost={batchReassignDisclosureHost}
            />
          )}
        </Flex>

        <Flex gap={"12px"} alignItems={"center"} sx={{ flexWrap: "wrap" }}>
          {!isVisitNotePage &&
            todoTaskFilter.toDoTask &&
            viewType !== "calender" && (
              <StyledButtonV2
                variant="ghost"
                disabled={selectedTaskModel?.length === 0}
                label="Batch Resolve"
                onClick={() =>
                  batchResolveDisclosureHost.open(selectedTaskModel)
                }
              />
            )}

          {!isVisitNotePage &&
            todoTaskFilter.toDoTask &&
            viewType !== "calender" && (
              <StyledButtonV2
                variant="ghost"
                disabled={selectedTaskModel?.length === 0}
                label="Batch Reassign"
                onClick={() =>
                  batchReassignDisclosureHost.open(selectedTaskModel)
                }
              />
            )}

          <SearchInput
            searchKey={searchStringKey ?? ""}
            onSearch={(v) => setSearchStringKey?.(v)}
            onClear={() => setSearchStringKey?.("")}
            placeholder={`Search by ${
              activeTab === "patient-centric-tasks"
                ? patientData
                  ? ""
                  : "Patient Name, "
                : ""
            }Assign to, Task Title`}
            name="searchString"
            startIcon={<SearchIcon sx={{ color: "#000", fontSize: 16 }} />}
            sx={{ width: "330px" }}
          />

          <GlobalFilter
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
            activeFilterCount={activeFilterCount}
          >
            <Flex width={"100%"} justifyContent={"end"}>
              <Button
                variant="text"
                onClick={() => {
                  setLocalFilters(DEFAULT_FILTERS);
                  setFilters(DEFAULT_FILTERS);
                }}
              >
                Clear Filter
              </Button>
            </Flex>
            {FILTER_KEYS.map((it) => {
              return it === "due_date" ? (
                <Accordion
                  key={it}
                  variant="outlined"
                  expanded={expanded[it]}
                  onChange={(_, newExpanded) =>
                    handleExpandClick(it, newExpanded)
                  }
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Text>{replaceUnderScoreAddSpace(it)}</Text>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: 1 }}>
                    <Flex flexDirection={"row"} gap={0.5} alignItems="center">
                      <Box flex={1}>
                        <CustomDatePicker
                          value={localFilters[it as keyof TaskFilters] ?? null}
                          onChange={(val) =>
                            setLocalFilters({
                              ...localFilters,
                              [it as keyof TaskFilters]: val,
                            })
                          }
                        />
                      </Box>
                      {/* ECRH-4232: explicit clear (×) for the Due Date filter */}
                      {localFilters[it as keyof TaskFilters] && (
                        <Tooltip title="Clear due date" placement="top" arrow>
                          <IconButton
                            size="small"
                            aria-label="Clear due date"
                            onClick={() => {
                              const cleared = {
                                ...localFilters,
                                [it as keyof TaskFilters]: null,
                              };
                              setLocalFilters(cleared);
                              setFilters(cleared);
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Flex>
                  </AccordionDetails>
                </Accordion>
              ) : it === "assign_to" ? (
                <Accordion
                  key={it}
                  variant="outlined"
                  expanded={expanded[it]}
                  onChange={(_, newExpanded) =>
                    handleExpandClick(it, newExpanded)
                  }
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Text>{replaceUnderScoreAddSpace(it)}</Text>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: 1 }}>
                    <Flex flexDirection={"column"} gap={1}>
                      <CustomSelectScroll
                        placeholder="Assign To"
                        options={providersList?.data?.content?.map((it) => ({
                          value: it?.uuid,
                          label: it?.firstName + " " + it?.lastName,
                        }))}
                        onSearch={(_, val) => handleProviderSearch(val)}
                        loading={providerFetching}
                        pagination={providerPagination}
                        setPagination={setProviderPagination}
                        totalElements={providersList?.data?.page?.totalElements}
                        value={
                          Object.keys(
                            localFilters[it as keyof TaskFilters] ?? {}
                          )?.[0] ?? undefined
                        }
                        onChange={(value, opt) => {
                          setLocalFilters({
                            ...localFilters,
                            [it as keyof TaskFilters]:
                              opt && value
                                ? { [opt.value]: opt.label }
                                : undefined,
                          });
                        }}
                      />
                    </Flex>
                  </AccordionDetails>
                </Accordion>
              ) : it === "assign_by" ? (
                <Accordion
                  key={it}
                  variant="outlined"
                  expanded={expanded[it]}
                  onChange={(_, newExpanded) =>
                    handleExpandClick(it, newExpanded)
                  }
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Text>{replaceUnderScoreAddSpace(it)}</Text>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: 1 }}>
                    <Flex flexDirection={"column"} gap={1}>
                      <CustomSelectScroll
                        placeholder="Assign By"
                        options={providersList?.data?.content?.map((it) => ({
                          value: it?.uuid,
                          label: it?.firstName + " " + it?.lastName,
                        }))}
                        onSearch={(_, val) => handleProviderSearch(val)}
                        loading={providerFetching}
                        pagination={providerPagination}
                        setPagination={setProviderPagination}
                        totalElements={providersList?.data?.page?.totalElements}
                        value={
                          Object.keys(
                            localFilters[it as keyof TaskFilters] ?? {}
                          )?.[0] ?? undefined
                        }
                        onChange={(value, opt) => {
                          setLocalFilters({
                            ...localFilters,
                            [it as keyof TaskFilters]:
                              opt && value
                                ? { [opt.value]: opt.label }
                                : undefined,
                          });
                        }}
                      />
                    </Flex>
                  </AccordionDetails>
                </Accordion>
              ) : it === "priority" ? (
                <Accordion
                  key={it}
                  variant="outlined"
                  expanded={expanded[it]}
                  onChange={(_, newExpanded) =>
                    handleExpandClick(it, newExpanded)
                  }
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Text>{replaceUnderScoreAddSpace(it)}</Text>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: 1 }}>
                    <Flex flexDirection={"column"} gap={1}>
                      <CustomSelect
                        placeholder="Select Task Priority"
                        options={[
                          { value: "HIGH", label: "High" },
                          { value: "MEDIUM", label: "Medium" },
                          { value: "LOW", label: "Low" },
                        ]}
                        value={
                          localFilters[it as keyof TaskFilters] ?? undefined
                        }
                        onChange={(value) =>
                          setLocalFilters({
                            ...localFilters,
                            [it as keyof TaskFilters]: value,
                          })
                        }
                      />
                    </Flex>
                  </AccordionDetails>
                </Accordion>
              ) : it === "action_type" ? (
                <Accordion
                  key={it}
                  variant="outlined"
                  expanded={expanded[it]}
                  onChange={(_, newExpanded) =>
                    handleExpandClick(it, newExpanded)
                  }
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Text>{replaceUnderScoreAddSpace(it)}</Text>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: 1 }}>
                    <Flex flexDirection={"column"} gap={1}>
                      <CustomSelect
                        placeholder="Select Action Type"
                        options={TASK_ACTIONS}
                        value={
                          localFilters[it as keyof TaskFilters] ?? undefined
                        }
                        onChange={(value) =>
                          setLocalFilters({
                            ...localFilters,
                            [it as keyof TaskFilters]: value,
                          })
                        }
                      />
                    </Flex>
                  </AccordionDetails>
                </Accordion>
              ) : (
                <Accordion
                  key={it}
                  variant="outlined"
                  expanded={expanded[it]}
                  onChange={(_, newExpanded) =>
                    handleExpandClick(it, newExpanded)
                  }
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Text>{replaceUnderScoreAddSpace(it)}</Text>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: 1 }}>
                    <Flex flexDirection={"column"} gap={1}>
                      <Input
                        textFieldProps={{
                          type: "text",
                          name: it,
                          value: localFilters[it as keyof TaskFilters] ?? "",
                          onChange: (e) =>
                            setLocalFilters({
                              ...localFilters,
                              [it as keyof TaskFilters]: e.target.value,
                            }),
                          placeholder: `Enter ${replaceUnderScoreAddSpace(it)}`,
                        }}
                      />
                    </Flex>
                  </AccordionDetails>
                </Accordion>
              );
            })}

            <Box minWidth={220}>
              <Flex width={"100%"} justifyContent={"end"}>
                <Button
                  variant="text"
                  sx={{ color: "gray" }}
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button variant="text" onClick={handleApply}>
                  Apply
                </Button>
              </Flex>
            </Box>
          </GlobalFilter>
          {viewType && !patientData && (
            <ToggleTaskCalenderViewButton
              view={viewType}
              handleAlignment={(_, newView) => {
                if (newView) setViewType?.(newView as "list" | "calender");
              }}
            />
          )}
          <StyledButtonV2
            variant="primary"
            shouldAddIcon
            label="Add Task"
            onClick={() => taskDisclosureHost.open()}
          />
          {/* <CreateTaskPopUp
            onOptionClick={(v) => {
              setTaskType(v);
              taskDisclosureHost.open();
            }}
          /> */}
        </Flex>
      </Flex>
    );
  }
);
